/** @jsxImportSource react */
import React, { useMemo, useCallback } from 'react';

const translations: { [key: string]: any } = {};

const rtlLanguages = ['ku', 'ar'];

interface I18nContextType {
    language: string;
    setLanguage: (lang: string) => void;
    t: (key: string, options?: { [key: string]: string | number }) => string;
    dir: 'ltr' | 'rtl';
    isRtl: boolean;
    isLoaded: boolean;
    formatNumber: (num: number, options?: Intl.NumberFormatOptions) => string;
    formatCurrency: (num: number | null | undefined, options?: Intl.NumberFormatOptions) => string;
    formatDate: (date: string | Date, options?: Intl.DateTimeFormatOptions) => string;
}

const I18nContext = React.createContext<I18nContextType | null>(null);

async function loadTranslations(lang: string) {
    if (translations[lang]) {
        return translations[lang];
    }
    try {
        const response = await fetch(`./${lang}.json`);
        if (!response.ok) {
            throw new Error(`Failed to load translation for ${lang}`);
        }
        const data = await response.json();
        translations[lang] = data;
        return data;
    } catch (error) {
        console.error(error);
        if (lang !== 'en' && !translations['en']) {
             const en_response = await fetch(`./en.json`);
             if(en_response.ok) {
                translations['en'] = await en_response.json();
             }
        }
        return {};
    }
}

type I18nProviderProps = {
    children: React.ReactNode;
};

export const I18nProvider: React.FC<I18nProviderProps> = ({ children }) => {
    const [language, setLanguageState] = React.useState(localStorage.getItem('language') || 'en');
    const [isLoaded, setIsLoaded] = React.useState(false);

    React.useEffect(() => {
        const init = async () => {
            setIsLoaded(false);
            try {
                await loadTranslations(language);
                if (!translations['en']) {
                    await loadTranslations('en');
                }
            } catch (e) {
                console.error("Failed to initialize i18n", e);
            } finally {
                setIsLoaded(true);
            }
        };
        init();
    }, [language]);

    const dir = useMemo<'ltr' | 'rtl'>(() => (rtlLanguages.includes(language) ? 'rtl' : 'ltr'), [language]);
    const isRtl = useMemo(() => dir === 'rtl', [dir]);
    
    React.useEffect(() => {
        document.documentElement.lang = language;
        document.documentElement.dir = dir;
    }, [language, dir]);

    const setLanguage = useCallback((lang: string) => {
        localStorage.setItem('language', lang);
        setLanguageState(lang);
    }, []);

    const t = useCallback((key: string, options?: { [key: string]: string | number }): string => {
        if (!isLoaded) return key;

        let translation = translations[language]?.[key] || translations['en']?.[key] || key;

        if (options && typeof translation === 'string') {
            Object.keys(options).forEach(optionKey => {
                const regex = new RegExp(`{{${optionKey}}}`, 'g');
                translation = translation.replace(regex, String(options[optionKey]));
            });
        }
        
        return translation;
    }, [isLoaded, language]);
    
    const formatNumber = useCallback((num: number, options?: Intl.NumberFormatOptions): string => {
        if (!isLoaded || isNaN(num)) return String(num);
        // Always use 'en' locale for number formatting to ensure English digits (0-9).
        return new Intl.NumberFormat('en', { ...options }).format(num);
    }, [isLoaded]);

    const formatCurrency = useCallback((num: number | null | undefined, options?: Intl.NumberFormatOptions): string => {
        if (!isLoaded || num === null || num === undefined || isNaN(num)) return '-';
        // Always use 'en' locale for currency formatting to ensure English digits (0-9).
        // The currency symbol position will be based on 'en' locale, which is appropriate for USD.
        const currencyOptions: Intl.NumberFormatOptions = {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            ...options
        };
        return new Intl.NumberFormat('en', currencyOptions).format(num);
    }, [isLoaded]);
    
    const formatDate = useCallback((date: string | Date, options?: Intl.DateTimeFormatOptions): string => {
        if (!isLoaded || !date) return String(date);
        try {
            const dateObj = typeof date === 'string' ? new Date(date) : date;
            const locale = language === 'ku' ? 'ku-IQ' : language;
            // Enforce latin numbering system for digits in dates across all languages.
            const finalOptions: Intl.DateTimeFormatOptions = { ...options, numberingSystem: 'latn' };
            return new Intl.DateTimeFormat(locale, finalOptions).format(dateObj);
        } catch (e) {
            return String(date);
        }
    }, [isLoaded, language]);

    const value = useMemo(() => ({
        language,
        setLanguage,
        t,
        dir,
        isRtl,
        isLoaded,
        formatNumber,
        formatCurrency,
        formatDate,
    }), [language, setLanguage, t, dir, isRtl, isLoaded, formatNumber, formatCurrency, formatDate]);

    const LoadingScreen = () => (
         React.createElement('div', { className: "flex justify-center items-center h-screen w-screen" },
            React.createElement('div', { className: "animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary" })
        )
    );

    return React.createElement(I18nContext.Provider, { value },
        isLoaded ? children : React.createElement(LoadingScreen)
    );
};

export const useTranslation = (): I18nContextType => {
    const context = React.useContext(I18nContext);
    if (!context) {
        throw new Error('useTranslation must be used within an I18nProvider');
    }
    return context;
};